import { Injectable } from '@angular/core';
import { $t } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

/**
 * Applies the global PrimeNG primary/surface palette. Unlike dark mode (a class
 * on <html>), the palette is global CSS variables on :root, so a user's saved
 * primary color would otherwise bleed onto the public/auth pages.
 *
 * applyDefault() resets to the Clubside brand default (Aura + Clubside green,
 * light) and is called on shell exit + cold-load of a public route, so the
 * login/register pages always look on-brand. The configurator re-applies the
 * user's own palette when the authenticated shell mounts.
 */
@Injectable({ providedIn: 'root' })
export class ThemeApplyService {
  // Routes rendered OUTSIDE any authenticated shell (see app.routes.ts).
  private readonly PUBLIC_PREFIXES = [
    '/login',
    '/register',
    '/forgotpassword',
    '/newpassword',
    '/verification',
    '/access',
    '/error',
  ];

  isPublicPath(path: string): boolean {
    const clean = (path || '/').split('?')[0].split('#')[0];
    return this.PUBLIC_PREFIXES.some(
      (p) => clean === p || clean.startsWith(p + '/'),
    );
  }

  /** Reset the primary/surface palette to the Clubside brand default (light). */
  applyDefault(): void {
    const green: Record<string, string> = {
      ...((Aura as any).primitive?.green ?? {}),
    };
    green['500'] = '#566e6a'; // Clubside green, mirroring the configurator override

    $t()
      .preset(Aura)
      .preset({
        semantic: {
          primary: green,
          colorScheme: {
            light: {
              primary: {
                color: '{primary.500}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.600}',
                activeColor: '{primary.700}',
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.100}',
                color: '{primary.700}',
                focusColor: '{primary.800}',
              },
            },
            dark: {
              primary: {
                color: '{primary.400}',
                contrastColor: '{surface.900}',
                hoverColor: '{primary.300}',
                activeColor: '{primary.200}',
              },
              highlight: {
                background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                focusBackground:
                  'color-mix(in srgb, {primary.400}, transparent 76%)',
                color: 'rgba(255,255,255,.87)',
                focusColor: 'rgba(255,255,255,.87)',
              },
            },
          },
        },
      })
      .use({ useDefaultOptions: true });
  }
}
